import {
  all, call, delay, put, select, takeLatest,
} from 'redux-saga/effects'
import { toast } from 'react-toastify'
import { actions as appActions } from '../state'
import {
  CheckStatus,
  CloseSalesAdjustmentPayload,
  GetBatches,
  GetFinishPayload,
  GetOverviewPayload,
  GetPreviousPendingRequestsPayload,
  GetReceivementInvoicesByReceivementOrRequestPayload,
  GetReceivementInvoicesPayload,
  GetReceivementWrongProductsPayload,
  GetSalesOverviewPayload,
  Invoice,
  OverviewProductStatusEnum,
  PatchBatchObservationPayload,
  PatchItemArObservationPayload,
  ReleaseReceivementPayload,
  ReleaseReceivementSwapReturnPayload,
  ResendToFinances,
  ReturnInvoice,
  SalesBatchesState,
  SalesReceivementProduct,
  SendJustificationReceiptsPayload,
  UploadReceiptsPayload,
  ConfirmTruckDriverPayload,
  Finish,
  ReceivementInvoices,
  ValidadeProductBarcodePayload,
  SignAuthorizationPayload,
} from './ReceivementTypes'
import apis from './ReceivementApi'
import { actions } from './ReceivementState'
import { PayloadAction } from '@reduxjs/toolkit'
import { ReturnCall } from '../types'
import { selectSalesHasProducts } from './ReceivementSelectors'
import { PrintInvoicesStepsEnum } from '../supplier/components/PrintInvoicesSection/PrintInvoicesSection.types'
import { InvoiceTypes } from 'src/components/Danfe/danfeTypes'
import { getDanfe } from 'src/components/Danfe/danfeApi'

function* persistBatchesState(products: SalesReceivementProduct[]) {
  const hasProducts = yield select(selectSalesHasProducts)

  const batchesState = products.filter((product) => product.status !== OverviewProductStatusEnum.WRONG)
    .flatMap((p) => p.batches.map((b) => ({
      batchItemOrderIds: b.idsBatchItemOrder,
      acceptedQuantity: b.acceptedQuantity,
      acceptDamage: b.acceptDamage,
      quantityPerBox: p.quantityPerBox,
      modelId: p.modelId,
      idReceivementAuthorization: p.idReceivementAuthorization,
    })))
    .reduce<SalesBatchesState>((a, b) => ({
      ...a,
      [b.batchItemOrderIds.join(',')]: {
        ...b,
        acceptDamage: false,
      },
    }), {})

  if (!hasProducts) {
    yield put(actions.setReceivementField({ key: 'batchesStateByBatchItemOrderIds', value: batchesState }))
  }
}

function* getOverview(data: PayloadAction<GetOverviewPayload>) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.getOverview> = yield call(
      apis.getOverview,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao carregar o resumo!')
      return
    }
    yield put(actions.setOverview({ overview: result.data }))
  } catch (error: any) {
    toast.error('Falha ao carregar o resumo!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* getSalesOverview(data: PayloadAction<GetSalesOverviewPayload>) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.getSalesOverview> = yield call(
      apis.getSalesOverview,
      data.payload,
    )

    if (!result.isOk) {
      if (result.data.data.type === 'ALREADY_ASSIGNED') {
        yield put(actions.setReceivementField({ key: 'userAssigned', value: result.data.data.buyerName }))
      } else {
        toast.error(result.data.data.message || 'Falha ao acessar o resumo!')
      }
      return
    }

    const batchesState: SalesBatchesState = yield select((s) => s.receivement.batchesStateByBatchItemOrderIds)

    let totalArStockDays = 0
    let totalReceivementStockDays = 0

    result.data.data.products.forEach((product) => {
      Object.values(batchesState).forEach((b) => {
        const { modelId, idReceivementAuthorization, stockDays } = b

        if (modelId === product.modelId && idReceivementAuthorization === product.idReceivementAuthorization) {
          totalArStockDays += stockDays || 0
        }
      })

      Object.values(batchesState).forEach((batchState) => {
        if (batchState.modelId === product.modelId) {
          totalReceivementStockDays += batchState.stockDays || 0
        }
      })

      product.totalArStockDays = totalArStockDays + product.currentStockDays
      product.totalReceivementStockDays = totalReceivementStockDays + product.currentStockDays

      product.batches.sort(((batch) => {
        const isDivergent = batch.isExceeding || batch.damageType !== 'NENHUM'
        return isDivergent ? -1 : 1
      }))

      totalArStockDays = 0
      totalReceivementStockDays = 0
    })

    yield call(persistBatchesState, result.data.data.products)

    yield put(actions.setReceivementField({ key: 'salesOverview', value: result.data.data }))

    if (result.data.statusReceivement !== 'Em espera pela aprovação de compras') {
      yield put(actions.setReceivementField({ key: 'awaitStatus', value: result.data.statusReceivement }))
    } else {
      yield put(actions.setReceivementField({ key: 'awaitStatus', value: '' }))
    }
  } catch (error: any) {
    toast.error('Falha ao carregar o resumo!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* clearUserAssigned(data: PayloadAction<GetSalesOverviewPayload>) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.clearUserAssigned> = yield call(
      apis.clearUserAssigned,
      data.payload,
    )

    if (!result.isOk) {
      toast.error('Falha ao desvincular comprador!')
      return
    }

    toast.success('Campo comprador desvinculado com sucesso!')
  } catch (error: any) {
    toast.error('Falha ao desvincular comprador!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* patchBatchObservation(data: PayloadAction<PatchBatchObservationPayload>) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.patchBatchObservation> = yield call(
      apis.patchBatchObservation,
      data.payload,
    )

    if (!result.isOk) {
      toast.error('Falha ao cadastrar observação!')
      return
    }

    const { batchId, observation } = data.payload

    yield put(actions.setSalesOverviewWrongProductObservation({ id: batchId, observation }))
    toast.success('Observação cadastrada com sucesso!')
  } catch (error: any) {
    toast.error('Falha ao cadastrar observação!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* patchItemArObservation(data: PayloadAction<PatchItemArObservationPayload>) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.patchItemArObservation> = yield call(
      apis.patchItemArObservation,
      data.payload,
    )

    if (!result.isOk) {
      toast.error('Falha ao cadastrar observação!')
      return
    }

    const { itemArId, observation } = data.payload
    yield put(actions.setSalesOverviewItemArObservation({ id: itemArId, observation }))
    toast.success('Observação cadastrada com sucesso!')
  } catch (error: any) {
    toast.error('Falha ao cadastrar observação!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* getFinish(
  data: PayloadAction<GetFinishPayload>,
) {
  try {
    const result: ReturnCall<typeof apis.getFinish> = yield call(
      apis.getFinish,
      data.payload,
    )

    if (!result.isOk) {
      throw new Error()
    } else if (!result.data) return

    yield put(actions.setFinish({ finish: result.data }))
    data.payload.optionalActionOnSuccess?.(result.data)
  } catch (err: any) {
    toast.error('Falha ao carregar relatorio final de recebimento!')
  } finally {
    data.payload.onFinishRequest?.()
  }
}

function* getReceivementWrongProducts(
  data: PayloadAction<GetReceivementWrongProductsPayload>,
) {
  yield put(actions.setLoading(true))

  try {
    const sortBy = data.payload.sortBy === 'invoice_number' ? 'id_lote' : data.payload.sortBy
    const result: ReturnCall<typeof apis.getReceivementWrongProducts> = yield call(apis.getReceivementWrongProducts, { ...data.payload, sortBy })

    if (!result.isOk) {
      toast.error('Falha ao carregar os produtos errados!')
      return
    }

    yield put(actions.setWrongProductsOverview({ wrongProductsOverview: result.data }))
  } catch (error: any) {
    toast.error('Falha ao carregar os produtos errados!')
  } finally {
    yield put(actions.setLoading(false))
  }
}

function* closeSalesAdjustment(
  data: PayloadAction<CloseSalesAdjustmentPayload>,
) {
  yield put(actions.setLoading(true))
  const { history } = data.payload

  try {
    const result: ReturnCall<typeof apis.closeSalesAdjustment> = yield call(apis.closeSalesAdjustment, { ...data.payload })

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao finalizar o recebimento!')
      return
    }

    yield put(actions.setProperty({ property: 'purchaseApproval', value: true }))
    toast.success('Ajuste de compras finalizado!')
    history.push('/')
  } catch (error: any) {
    toast.error('Falha ao finalizar o ajuste!')
  } finally {
    yield put(actions.setLoading(false))
  }
}

export function* getReceivementInvoicesHtml(invoices: Invoice[] | ReturnInvoice[], type: InvoiceTypes) {
  try {
    // Nota de devolução só recebe invoiceKeyReturn quando a NFD é confirmada pela
    // integração; até lá a chave vem nula e não há DANFE a buscar.
    const invoiceKeys = invoices
      .map((invoice) => (type === InvoiceTypes.RECEIVEMENT ? invoice.invoiceAccessKey : invoice.invoiceKeyReturn))
      .filter((invoiceKey): invoiceKey is string => !!invoiceKey)

    if (!invoiceKeys.length) return []

    const invoicesHtmlResults = yield all(invoiceKeys.map((invoiceKey) => call(getDanfe, {
      invoiceKey,
      type,
    })))

    return invoicesHtmlResults.map((invoiceHtml) => invoiceHtml.data)
  } catch (err: any) {
    throw new Error()
  }
}

function* getReceivementInvoices(
  data: PayloadAction<GetReceivementInvoicesPayload>,
) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.getReceivementInvoices> = yield call(
      apis.getReceivementInvoices,
      data.payload,
    )

    if (!result.isOk) {
      const { message } = result?.data?.data

      if (message) {
        toast.error(message)
        return
      }
      throw new Error()
    }

    const receivementInvoices = result.data

    const [acceptedInvoicesHtml, returnInvoicesHtml, rejectedInvoicesHtml] = yield all([
      getReceivementInvoicesHtml(receivementInvoices.invoices, InvoiceTypes.RECEIVEMENT),
      getReceivementInvoicesHtml(receivementInvoices.invoicesReturn, InvoiceTypes.RETURN),
      getReceivementInvoicesHtml(receivementInvoices.invoicesRejected, InvoiceTypes.RECEIVEMENT),
    ])

    const foundInvoiceHtml = acceptedInvoicesHtml.length || returnInvoicesHtml.length || rejectedInvoicesHtml.length

    if (foundInvoiceHtml) {
      const receivementInvoicesHtml = {
        idReceivement: receivementInvoices.idReceivement,
        idReceivementStatus: receivementInvoices.idReceivementStatus,
        integrationFail: receivementInvoices.integrationFail,
        invoices: receivementInvoices.invoices,
        acceptedInvoicesHtml,
        returnInvoicesHtml,
        rejectedInvoicesHtml,
      }

      yield put(actions.setCurrentPrintInvoicesStep(PrintInvoicesStepsEnum.START_PRINT_OR_RETURN))
      yield put(actions.setReceivementInvoicesHtml({ receivementInvoicesHtml }))
      yield put(actions.setReceivementInvoices({ receivementInvoices }))
    } else {
      toast.error('Não foram encontrados documentos html vinculados as notas fiscais do recebimento em atendimento')
    }
  } catch (err: any) {
    toast.error('Falha ao carregar notas fiscais do recebimento em atendimento com essa senha de atendimento')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* uploadReceipts(payload: PayloadAction<UploadReceiptsPayload>) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.uploadReceipts> = yield call(
      apis.uploadReceipts,
      payload.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || `Falha ao salvar o(s) canhoto(s) do recebimento ${payload.payload.idReceivement} `)
      payload.payload.setNextButtonIsDisabled(true)
      return
    }

    if (result.isOk) {
      payload.payload.setNextButtonIsDisabled(false)
    }

    toast.success('Canhoto(s) salvo(s) com sucesso!')
  } catch (error: any) {
    toast.error(`Falha ao salvar o(s) canhoto(s) do recebimento ${payload.payload.idReceivement} `)
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* sendJustificationReceipts(payload: PayloadAction<SendJustificationReceiptsPayload>) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.sendJustificationReceipts> = yield call(
      apis.sendJustificationReceipts,
      payload.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || `Falha ao salvar a justificativa para não assinatura do canhoto do recebimento ${payload.payload.idReceivement} `)
      payload.payload.setNextButtonIsDisabled(true)
      return
    }

    if (result.isOk) {
      payload.payload.setNextButtonIsDisabled(false)
    }

    toast.success('Justificativa salva com sucesso!')
  } catch (error: any) {
    toast.error(`Falha ao salvar a justificativa para não assinatura do canhoto do recebimento ${payload.payload.idReceivement}`)
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* resendToFinances(data: PayloadAction<ResendToFinances>) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.resendToFinances> = yield call(apis.resendToFinances, { ...data.payload })

    if (!result.isOk) {
      toast.error('Falha ao reenviar notas ao financeiro!')
      return
    }

    const receivementInvoices = result.data

    const [acceptedInvoicesHtml, returnInvoicesHtml, rejectedInvoicesHtml] = yield all([
      getReceivementInvoicesHtml(receivementInvoices.invoices, InvoiceTypes.RECEIVEMENT),
      getReceivementInvoicesHtml(receivementInvoices.invoicesReturn, InvoiceTypes.RETURN),
      getReceivementInvoicesHtml(receivementInvoices.invoicesRejected, InvoiceTypes.RECEIVEMENT),
    ])

    const foundInvoiceHtml = acceptedInvoicesHtml.length || returnInvoicesHtml.length || rejectedInvoicesHtml.length

    if (foundInvoiceHtml) {
      const receivementInvoicesHtml = {
        idReceivement: receivementInvoices.idReceivement,
        idReceivementStatus: receivementInvoices.idReceivementStatus,
        integrationFail: receivementInvoices.integrationFail,
        invoices: receivementInvoices.invoices,
        acceptedInvoicesHtml,
        returnInvoicesHtml,
        rejectedInvoicesHtml,
      }

      yield put(actions.setCurrentPrintInvoicesStep(PrintInvoicesStepsEnum.START_PRINT_OR_RETURN))
      yield put(actions.setReceivementInvoicesHtml({ receivementInvoicesHtml }))
      yield put(actions.setReceivementInvoices({ receivementInvoices }))
    }
    toast.success('Notas reenviadas ao financeiro!')
  } catch (error: any) {
    toast.error('Falha ao reenviar notas ao financeiro!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* releaseReceivement(data: PayloadAction<ReleaseReceivementPayload>) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.releaseReceivement> = yield call(
      apis.releaseReceivement,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao liberar o recebimento')
      return
    }

    toast.success('Recebimento liberado com sucesso!')
    yield delay(2000)
    window.location.reload()
  } catch (error: any) {
    toast.error('Falha ao liberar o recebimento')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* getStatusInvoiceReturn(data: PayloadAction<CheckStatus>) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.getStatusInvoiceReturn> = yield call(apis.getStatusInvoiceReturn, { ...data.payload })

    if (!result.isOk) {
      toast.error('Falha ao verificar status da(s) nota(s) de devolução!')
      return
    }
    toast.success('Status da(s) nota(s) de devolução atualizado com sucesso!')
    yield put(actions.setInvoicesReturn({ invoicesReturn: result.data }))

    const returnInvoices = result.data

    const [updatedHtml] = yield all([getReceivementInvoicesHtml(returnInvoices, InvoiceTypes.RETURN)])

    yield put(actions.setInvoicesReturnHtml({ returnInvoicesHtml: updatedHtml }))
  } catch (error: any) {
    toast.error('Falha ao verificar status da(s) nota(s) de devolução!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* getReceivementBatches(data: PayloadAction<GetBatches>) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.getReceivementBatches> = yield call(
      apis.getReceivementBatches,
      data.payload,
    )

    if (!result.isOk) {
      toast.error('Falha ao buscar os lotes do recebimento')
      return
    }

    yield put(actions.setReceivementBatches({ batches: result.data.data }))
    yield put(actions.setDirectGondolaPrintLimit({ directGondolaPrintLimit: result.data.directGondolaPrintLimit }))
  } catch (error: any) {
    toast.error('Falha ao buscar os lotes do recebimento')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* getPreviousPendingRequests(
  data: PayloadAction<GetPreviousPendingRequestsPayload>,
) {
  yield put(appActions.isLoading(true))
  const { idSupplier } = data.payload

  try {
    const result: ReturnCall<typeof apis.getPreviousPendingRequests> = yield call(
      apis.getPreviousPendingRequests,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || `Não foi possível buscar solitações pendentes para o fornecedor: ${idSupplier}!`)
      return
    }

    yield put(actions.setProperty({ property: 'previousPendingRequestInfo', value: result.data }))
  } catch (error) {
    toast.error(`Não foi possível buscar solitações pendentes para o fornecedor: ${idSupplier}!`)
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* confirmTruckDriver(data: PayloadAction<ConfirmTruckDriverPayload>) {
  try {
    const result: ReturnCall<typeof apis.confirmTruckDriver> = yield call(
      apis.confirmTruckDriver,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao confirmar motorista')
      return
    }

    const finish: Finish = yield select((s) => s.receivement.finish)

    yield put(actions.setFinish({
      finish: {
        ...finish,
        requestsTagsInfo: result.data,
      },
    }))

    yield put(actions.setTruckDriver(data.payload.isAvailable))

    const receivementInvoices: ReceivementInvoices = yield select((s) => s.receivement.receivementInvoices)
    yield put(actions.setReceivementInvoices({
      receivementInvoices: {
        ...receivementInvoices,
        isTruckDriverAvailableReturn: data.payload.isAvailable,
      },
    }))
  } catch (error: any) {
    toast.error('Falha ao confirmar motorista!')
  } finally {
    data.payload.setDisableConfirmTruckDriver(false)
  }
}

function* getReceivementInvoicesByReceivementOrRequest(
  data: PayloadAction<GetReceivementInvoicesByReceivementOrRequestPayload>,
) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.getReceivementInvoicesByReceivementOrRequest> = yield call(
      apis.getReceivementInvoicesByReceivementOrRequest,
      data.payload,
    )

    if (!result.isOk) {
      const { message } = result?.data?.data

      if (message) {
        toast.error(message)
        return
      }
      throw new Error()
    }

    const receivementInvoices = result.data

    const [acceptedInvoicesHtml, returnInvoicesHtml, rejectedInvoicesHtml] = yield all([
      getReceivementInvoicesHtml(receivementInvoices.invoices, InvoiceTypes.RECEIVEMENT),
      getReceivementInvoicesHtml(receivementInvoices.invoicesReturn, InvoiceTypes.RETURN),
      getReceivementInvoicesHtml(receivementInvoices.invoicesRejected, InvoiceTypes.RECEIVEMENT),
    ])

    const foundInvoiceHtml = acceptedInvoicesHtml.length || returnInvoicesHtml.length || rejectedInvoicesHtml.length

    if (foundInvoiceHtml) {
      const receivementInvoicesHtml = {
        idReceivement: receivementInvoices.idReceivement,
        idReceivementStatus: receivementInvoices.idReceivementStatus,
        integrationFail: receivementInvoices.integrationFail,
        invoices: receivementInvoices.invoices,
        acceptedInvoicesHtml,
        returnInvoicesHtml,
        rejectedInvoicesHtml,
      }

      yield put(actions.setCurrentPrintInvoicesStep(PrintInvoicesStepsEnum.START_PRINT_OR_RETURN))
      yield put(actions.setReceivementInvoicesHtml({ receivementInvoicesHtml }))
      yield put(actions.setReceivementInvoices({ receivementInvoices }))
    } else {
      toast.error('Não foram encontrados documentos html vinculados as notas fiscais do recebimento em atendimento')
    }
  } catch (err: any) {
    toast.error('Falha ao carregar notas fiscais do recebimento em atendimento com essa senha de atendimento')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* releaseReceivementSwapReturn(data: PayloadAction<ReleaseReceivementSwapReturnPayload>) {
  yield put(appActions.isLoading(true))

  try {
    const { onSuccess } = data.payload

    const result: ReturnCall<typeof apis.releaseReceivementSwapReturn> = yield call(
      apis.releaseReceivementSwapReturn,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao liberar o recebimento')
      return
    }

    onSuccess()
  } catch (error: any) {
    toast.error('Falha ao liberar o recebimento')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* validateProductBarcode(data: PayloadAction<ValidadeProductBarcodePayload>) {
  yield put(appActions.isLoading(true))

  try {
    const { onSuccess } = data.payload

    const result: ReturnCall<typeof apis.validateProductBarcode> = yield call(
      apis.validateProductBarcode,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao validar barcode de produto')
      return
    }

    onSuccess()
  } catch (error: any) {
    toast.error('Falha ao validar barcode de produto')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* getOpenedInvoicesReturn(data: PayloadAction<number>) {
  yield put(appActions.isLoading(true))

  try {
    if (!data.payload) {
      toast.error('ID do recebimento não informado')
      return
    }

    const result: ReturnCall<typeof apis.getOpenedInvoicesReturn> = yield call(
      apis.getOpenedInvoicesReturn,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao buscar nota(s) de devolução')
      return
    }

    yield put(actions.setInvoicesReturn({ invoicesReturn: result.data }))

    const returnInvoices = result.data

    const [updatedHtml] = yield all([getReceivementInvoicesHtml(returnInvoices, InvoiceTypes.RETURN)])

    yield put(actions.setInvoicesReturnHtml({ returnInvoicesHtml: updatedHtml }))
  } catch (error: any) {
    toast.error('Falha ao buscar nota(s) de devolução!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* signAuthorization(data: PayloadAction<SignAuthorizationPayload>) {
  yield put(appActions.isLoading(true))

  const { accessKey, onSuccess } = data.payload

  try {
    const result = yield call(apis.signAuthorization, accessKey)

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao confirmar ação')
      return
    }

    yield put(actions.setInvoiceSigned({ accessKey, isSigned: result.data.isSigned }))

    onSuccess()
  } catch (error: any) {
    toast.error('Falha ao confirmar ação!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export default function* () {
  yield all([
    takeLatest(actions.getOverview.type, getOverview),
    takeLatest(actions.getSalesOverview.type, getSalesOverview),
    takeLatest(
      actions.getReceivementWrongProducts.type,
      getReceivementWrongProducts,
    ),
    takeLatest(actions.getFinish.type, getFinish),
    takeLatest(actions.patchBatchObservation.type, patchBatchObservation),
    takeLatest(actions.patchItemArObservation.type, patchItemArObservation),
    takeLatest(actions.closeSalesAdjustment, closeSalesAdjustment),
    takeLatest(actions.getReceivementInvoices, getReceivementInvoices),
    takeLatest(actions.uploadReceipts, uploadReceipts),
    takeLatest(actions.resendToFinances, resendToFinances),
    takeLatest(actions.getStatusInvoiceReturn, getStatusInvoiceReturn),
    takeLatest(actions.clearUserAssigned.type, clearUserAssigned),
    takeLatest(actions.releaseReceivement, releaseReceivement),
    takeLatest(actions.sendJustificationReceipts, sendJustificationReceipts),
    takeLatest(actions.getReceivementBatches, getReceivementBatches),
    takeLatest(actions.getPreviousPendingRequests.type, getPreviousPendingRequests),
    takeLatest(actions.confirmTruckDriver, confirmTruckDriver),
    takeLatest(actions.getReceivementInvoicesByReceivementOrRequest, getReceivementInvoicesByReceivementOrRequest),
    takeLatest(actions.releaseReceivementSwapReturn, releaseReceivementSwapReturn),
    takeLatest(actions.validateProductBarcode, validateProductBarcode),
    takeLatest(actions.getOpenedInvoicesReturn, getOpenedInvoicesReturn),
    takeLatest(actions.signAuthorization, signAuthorization),
  ])
}
